import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { useRecords } from '@/contexts/record-context'
import { useNotification } from '@/hooks/use-notification'
import { BottomNav, type TabId } from '@/components/bottom-nav'
import { WechatGuide } from '@/components/wechat-guide'
import { PwaInstallBanner } from '@/components/pwa-install-banner'
import { RecoveryModal } from '@/components/recovery-modal'
import { HomePage } from '@/pages/home-page'
import { StatsPage } from '@/pages/stats-page'
import { LeaderboardPage } from '@/pages/leaderboard-page'
import { SettingsPage } from '@/pages/settings-page'
import { cn } from '@/lib/utils'

function App() {
  const [activeTab, setActiveTab] = useState<TabId>('home')
  const [showWechatGuide, setShowWechatGuide] = useState(false)
  const { ready, loading } = useAuth()
  const { fetchTodayRecords } = useRecords()
  const { isWechat } = useNotification()

  // Check WeChat environment
  useEffect(() => {
    if (isWechat) {
      setShowWechatGuide(true)
    }
  }, [isWechat])

  // Fetch today's records on mount
  useEffect(() => {
    if (ready) {
      fetchTodayRecords()
    }
  }, [ready, fetchTodayRecords])

  if (!ready || loading) {
    return (
      <div className="flex items-center justify-center min-h-[100dvh] bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">正在初始化...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-lg mx-auto bg-background min-h-[100dvh] relative">
      {/* Main content */}
      <main className={cn(
        activeTab === 'home' && 'pb-14',
        activeTab === 'stats' && 'pb-14',
        activeTab === 'leaderboard' && 'pb-14',
        activeTab === 'settings' && 'pb-14',
      )}>
        {activeTab === 'home' && <HomePage />}
        {activeTab === 'stats' && <StatsPage />}
        {activeTab === 'leaderboard' && <LeaderboardPage />}
        {activeTab === 'settings' && <SettingsPage />}
      </main>

      {/* Bottom nav */}
      <BottomNav activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Global overlays */}
      <WechatGuide visible={showWechatGuide} onDismiss={() => setShowWechatGuide(false)} />
      <PwaInstallBanner />
      <RecoveryModal />
    </div>
  )
}

export default App
